import {
  SIGNAL_NAMES,
  signalFilled,
  useConciergeState,
  type ConciergeSignals,
  type SignalName,
  type SignalSource,
} from './conciergeState'
import { recommendationReadiness } from './recommendationRequest'
import { trackConciergeEvent } from './conciergeEventTracking'
import type { ConciergeInputVariantName } from './ConciergeInputVariant.vue'

type SignalSnapshot = { filled: boolean, key: string, origin: SignalSource | null }

function snapshotSignal (signal: ConciergeSignals[SignalName]): SignalSnapshot {
  return { filled: signalFilled(signal), key: JSON.stringify(signal.value), origin: signal.source }
}

function snapshotSignals (signals: ConciergeSignals): Record<SignalName, SignalSnapshot> {
  return Object.fromEntries(
    SIGNAL_NAMES.map(name => [name, snapshotSignal(signals[name])]),
  ) as Record<SignalName, SignalSnapshot>
}

// Signal + readiness events, driven off the shared store so one watcher covers both input
// variants. Diffs against a snapshot so the free-text parser's per-keystroke rewrite and
// signals restored from a previous page (the on-mount baseline) don't re-fire.
export function useConciergeFunnelTracking (variant: () => ConciergeInputVariantName) {
  const state = useConciergeState()

  let lastSignals = snapshotSignals(state.signals)
  let wasReady = recommendationReadiness(state.signals).ready

  watch(
    () => state.signals,
    (signals) => {
      for (const name of SIGNAL_NAMES) {
        const before = lastSignals[name]
        const after = snapshotSignal(signals[name])
        if (before.filled === after.filled && before.key === after.key) continue

        const action = !before.filled ? 'filled' : after.filled ? 'edited' : 'cleared'
        trackConciergeEvent('signal_filled', {
          variant: variant(),
          signalKind: name,
          signalOrigin: (after.filled ? after.origin : before.origin) ?? undefined,
          metadata: { action },
        })
      }
      lastSignals = snapshotSignals(signals)

      const ready = recommendationReadiness(signals).ready
      if (ready && !wasReady)
        trackConciergeEvent('readiness_reached', { variant: variant() })
      wasReady = ready
    },
    { deep: true },
  )
}
